import { useMutation } from '@tanstack/react-query';

import { CacheManager } from '@/data/hook/query/v2/CacheFacade';
import {
  getEmptyActivitiesBySlug,
  updateCoursePath,
} from '@/data/services/coursePathServices';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import CoursePath from '@/models/CoursePath';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useTranslation } from 'react-i18next';
import { EmptyActivity } from '@/models/_deprecated/Activity';
import Lesson from '@/models/Lesson';
import { listLessonsByCourseSlug } from '@/data/services/lessonServices';

export class EmptyActivitiesError extends Error {
  constructor(public payload: EmptyActivity[]) {
    super('Empty activities found');
  }
}

export class LessonNotPublishedError extends Error {
  constructor(public payload: Lesson[]) {
    super('Lesson not published');
  }
}

export type ValidationError = EmptyActivitiesError | LessonNotPublishedError;

type UseContPedStatusProps = {
  coursePath: CoursePath;
  cache: CacheManager<CoursePath>;
  onInvalidate: (error: ValidationError) => void;
};

const useContPedStatus = ({
  coursePath,
  cache,
  onInvalidate,
}: UseContPedStatusProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseWorkspace',
  });

  const { mutate: validate, isPending: isValidating } = useMutation({
    mutationFn: () => handleValidate(coursePath),
    onError: error => {
      onInvalidate(error as ValidationError);
    },
    onSuccess: () => {
      publish();
    },
  });
  const { mutate: publish, isPending: isPublishing } = useMutation({
    mutationFn: () =>
      updateCoursePath(coursePath.slug, {
        status: VersioningStatusEnum.BETA,
      }),
    onSuccess: data => {
      alert.success(t('publishSuccess'));
      cache.merge(data);
    },
    onError: error => {
      alert.error(getErrorMessage(error));
    },
  });

  const isPending = isPublishing || isValidating;

  return {
    validateAndPublish: validate,
    isPublishing,
    isValidating,
    isPending,
  };
};

export default useContPedStatus;

const handleValidate = async (coursePath: CoursePath) => {
  const lessonsPage = await listLessonsByCourseSlug(coursePath.slug);
  if (!lessonsPage.meta.pagination.count) {
    throw new LessonNotPublishedError(lessonsPage.results);
  }
  const emptyActivities = await getEmptyActivitiesBySlug(coursePath.slug);
  if (emptyActivities.length > 0) {
    throw new EmptyActivitiesError(emptyActivities);
  }
};
